import {
  BaseQueryFn,
  FetchArgs,
  FetchBaseQueryError,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import { datadogRum } from '@datadog/browser-rum';

import { VITE_IQ_SERVICES_API_GATEWAY, VITE_IS_DEV } from '~/lib/constants/env';
import { BaseApiConfig } from '~/lib/Api';
import { security } from '~/store/security';
import { isTestEnvironment } from '~/lib/util/environment.util';

// Validate IQ Services API Gateway URL against expected Equip domain pattern to prevent SSRF
const IQ_BASE_URL_PATTERN = /^https:\/\/[\w.-]+\.equip\.health$/;

const getValidatedIqBaseUrl = (): string | null => {
  const raw = VITE_IQ_SERVICES_API_GATEWAY;
  if (!raw) return null;
  let endIndex = raw.length;
  // 47 is the ASCII/Unicode code point for forward slash '/'
  // Strip trailing slashes to normalize the base URL
  while (endIndex > 0 && raw.codePointAt(endIndex - 1) === 47) {
    endIndex -= 1;
  }
  const sanitized = raw.slice(0, endIndex);
  if (!IQ_BASE_URL_PATTERN.test(sanitized)) {
    datadogRum.addError(
      new Error('VITE_IQ_SERVICES_API_GATEWAY does not match expected pattern'),
      {
        extra: {
          message: 'IQ Services API Gateway URL validation failed',
          sanitizedUrl: sanitized,
        },
        tags: { logFrom: 'iqBaseQuery.getValidatedIqBaseUrl' },
      },
    );
    return null;
  }
  return sanitized;
};

// Validate URL at module initialization for immediate security feedback
const VALIDATED_IQ_BASE_URL = getValidatedIqBaseUrl();

if (!VALIDATED_IQ_BASE_URL && !isTestEnvironment) {
  throw new Error(
    'VITE_IQ_SERVICES_API_GATEWAY is not configured or invalid. ' +
      'This environment variable is required for IQ Services API.',
  );
}

// In local development, use the Vite proxy to avoid CORS issues.
// The IQ API only allows requests from CloudFront, not from local.equip.health.
// In deployed environments (dev/qa/staging/prod), use the validated URL directly.
const getIqBaseUrl = (): string => {
  // Use Vite's built-in DEV flag to detect development mode
  // In production builds, VITE_IS_DEV is always false
  // In tests, isTestEnvironment is true, so we skip the proxy
  const isDevelopment = VITE_IS_DEV;

  if (isDevelopment && !isTestEnvironment) {
    return '/iq-api';
  }
  return VALIDATED_IQ_BASE_URL || '';
};

const iqBaseQuery = fetchBaseQuery({
  baseUrl: getIqBaseUrl(),
  prepareHeaders: async (headers) => {
    // Use the same token retrieval and audience as the main API
    const access_token = await security.getAccessTokenSilently()({
      authorizationParams: {
        audience: BaseApiConfig.auth0Audience,
        scope: 'offline_access',
      },
    });

    if (access_token) {
      headers.set('Authorization', `Bearer ${access_token}`);
      // NOTE: uniqueRequestId is intentionally NOT set here to avoid CORS preflight issues
      // with the IQ Services API. The IQ API does not include uniqueRequestId in its
      // Access-Control-Allow-Headers CORS configuration.
    }
    return headers;
  },
  timeout: 30000,
});

const iqBaseQueryWithRetry = retry(iqBaseQuery, { maxRetries: 0 });

const iqBaseQueryWithErrorLogging: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  try {
    const result = await iqBaseQueryWithRetry(args, api, extraOptions);

    if (result.error) {
      const { error } = result;
      const errorData = error.data
        ? JSON.stringify(error.data)
        : JSON.stringify(error);

      // Report all errors to Datadog for observability
      datadogRum.addError(
        new Error(`IQ API error: ${api.endpoint}`),
        {
          extra: {
            endpoint: api.endpoint,
            status: error.status,
            data: errorData,
          },
          tags: { logFrom: 'iqBaseQuery' },
        },
      );

      if (!isTestEnvironment) {
        // FETCH_ERROR indicates network/CORS issues - downgrade to warning
        if (error.status === 'FETCH_ERROR') {
          console.warn(`IQ API network error, url: ${api.endpoint}`, {
            data: errorData,
            endpoint: api.endpoint,
            status: error.status,
          });
        } else {
          // HTTP errors (4xx, 5xx) remain as errors
          console.error(`IQ API error, url: ${api.endpoint}`, {
            data: errorData,
            endpoint: api.endpoint,
            status: error.status,
          });
        }
      }
    }

    return result;
  } catch (error) {
    console.error(error, {
      endpoint: api.endpoint,
    });

    throw error;
  }
};

export default iqBaseQueryWithErrorLogging;
